import { useState, useEffect, useCallback } from 'react';

interface UseViewedMemoriesReturn {
  viewedMemories: Set<string>;
  isMemoryViewed: (memoryId: string) => boolean;
  markAsViewed: (memoryId: string) => void;
  markAsUnviewed: (memoryId: string) => void;
  markAllAsViewed: (memoryIds: string[]) => void;
  markAllAsUnviewed: () => void;
}

/**
 * Custom hook to manage viewed/unviewed memory state using localStorage
 * Stores memory IDs that have been viewed by the current user
 */
export const useViewedMemories = (storyId?: string): UseViewedMemoriesReturn => {
  const [viewedMemories, setViewedMemories] = useState<Set<string>>(new Set());

  // Generate storage key based on story ID to keep views separate per story
  const getStorageKey = useCallback(() => {
    return storyId ? `memvy_viewed_memories_${storyId}` : 'memvy_viewed_memories';
  }, [storyId]);

  // Load viewed memories from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(getStorageKey());
      if (stored) {
        const parsed = JSON.parse(stored);
        setViewedMemories(new Set(parsed));
      }
    } catch (error) {
      console.error('Error loading viewed memories from localStorage:', error);
    }
  }, [getStorageKey]);

  // Save to localStorage whenever viewedMemories changes
  const saveToLocalStorage = useCallback(
    (memories: Set<string>) => {
      try {
        localStorage.setItem(getStorageKey(), JSON.stringify(Array.from(memories)));
      } catch (error) {
        console.error('Error saving viewed memories to localStorage:', error);
      }
    },
    [getStorageKey],
  );

  // Check if a memory has been viewed
  const isMemoryViewed = useCallback(
    (memoryId: string): boolean => {
      return viewedMemories.has(memoryId);
    },
    [viewedMemories],
  );

  // Mark a single memory as viewed
  const markAsViewed = useCallback(
    (memoryId: string) => {
      setViewedMemories((prev) => {
        const updated = new Set(prev);
        updated.add(memoryId);
        saveToLocalStorage(updated);
        return updated;
      });
    },
    [saveToLocalStorage],
  );

  // Mark a single memory as unviewed
  const markAsUnviewed = useCallback(
    (memoryId: string) => {
      setViewedMemories((prev) => {
        const updated = new Set(prev);
        updated.delete(memoryId);
        saveToLocalStorage(updated);
        return updated;
      });
    },
    [saveToLocalStorage],
  );

  // Mark all provided memories as viewed
  const markAllAsViewed = useCallback(
    (memoryIds: string[]) => {
      setViewedMemories((prev) => {
        const updated = new Set([...prev, ...memoryIds]);
        saveToLocalStorage(updated);
        return updated;
      });
    },
    [saveToLocalStorage],
  );

  // Mark all memories as unviewed (clear all)
  const markAllAsUnviewed = useCallback(() => {
    setViewedMemories(new Set());
    saveToLocalStorage(new Set());
  }, [saveToLocalStorage]);

  return {
    viewedMemories,
    isMemoryViewed,
    markAsViewed,
    markAsUnviewed,
    markAllAsViewed,
    markAllAsUnviewed,
  };
};
